import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { ContentType, Heading, useDeviceSize } from '@atlassian/learning-components';
import { useLocation, useNavigate } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { media } from '@atlaskit/primitives/responsive';
import { N800 } from '@atlaskit/theme/colors';

import { TagList } from 'src/components/TagList';
import { getHubUrl } from 'src/utils/getUrl';
import { ContentHubType, ResourceType } from 'src/constants';
import { generateBreadcrumbLinks } from 'src/components/utils/generateBreadcrumbLinks';
import { getContentHeaderContent } from 'src/components/utils/getContentHeaderContent';
import { ContentfulLearningPath } from 'src/graphql/queries/GetLearningPath';
import { ContentfulSmCourse } from 'src/graphql/queries/GetSmCourse';
import { GetLearningPathId } from 'src/graphql/queries/GetLearningPathId';
import { getOptions } from 'src/pages/LessonPage/children/utils/getOptions';

interface ContentHeaderProps {
  content: ContentfulLearningPath | GetLearningPathId | ContentfulSmCourse;
  contentType: ContentType.LEARNING_PATH | ContentType.COURSE;
  learningPath?: GetLearningPathId;
}

export const ContentHeader: React.FC<ContentHeaderProps> = ({ content, contentType, learningPath }) => {
  const isLearningPath = contentType === ContentType.LEARNING_PATH;
  const currentLearningPath = isLearningPath ? (content as ContentfulLearningPath) : learningPath;
  const course = !isLearningPath ? (content as ContentfulSmCourse) : undefined;

  const deviceWidth = useDeviceSize();
  const inspectorProps = useContentfulInspectorMode();
  const location = useLocation();
  const navigate = useNavigate();

  const { title, tagline, product, productRole, learningPathSummary, overview } = getContentHeaderContent(
    content,
    isLearningPath,
    location.pathname,
  );

  const hasRoles = productRole && productRole.length > 0;
  const isAllLearningPathsHubIncluded = location.pathname?.includes(getHubUrl(ContentHubType.LEARNING_PATHS));
  const isLearningPathIncluded = location.pathname?.includes('path/');

  const isBreadcrumbVisible = isLearningPath
    ? isAllLearningPathsHubIncluded
    : isAllLearningPathsHubIncluded || isLearningPathIncluded;

  const breadcrumbLinks = generateBreadcrumbLinks(currentLearningPath, course);
  const contentTypePrefix = isLearningPath ? ResourceType.LearningPath : ResourceType.SmCourse;

  return (
    <div css={headerContainerStyles} data-testid={`${contentTypePrefix}-header`}>
      <div css={headingAndSubheadingStyles} data-testid="heading-and-subheader">
        {isBreadcrumbVisible && breadcrumbLinks.length > 0 && (
          <div css={breadcrumbContainerStyles} data-testid="breadcrumbs-container">
            <Breadcrumbs itemsBeforeCollapse={0} itemsAfterCollapse={1}>
              {breadcrumbLinks.map((link, index) => (
                <BreadcrumbsItem
                  key={link.label}
                  text={link.label}
                  href={link.link}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(link.link);
                  }}
                  data-testid={`breadcrumb-link-${index}`}
                />
              ))}
            </Breadcrumbs>
          </div>
        )}
        <Heading level={1} color={token('color.text')} data-testid={`${contentTypePrefix}-title`}>
          {title}
        </Heading>
        {tagline && (
          <span css={subHeadingStyles} data-testid={`${contentTypePrefix}-tagline`}>
            {tagline}
          </span>
        )}
        {learningPathSummary && (
          <section
            {...inspectorProps({ entryId: learningPath?.sys.id ?? '', fieldId: 'overview' })}
            data-testid="learning-path-summary"
            css={learningPathOverviewStyles}
          >
            <div css={markdownContainerStyles}>
              {documentToReactComponents(learningPathSummary.json, getOptions(learningPathSummary, deviceWidth))}
            </div>
          </section>
        )}
        {overview && (
          <section
            {...inspectorProps({ entryId: course?.sys.id ?? '', fieldId: 'overview' })}
            data-testid="course-overview"
            css={learningPathOverviewStyles}
          >
            <div css={markdownContainerStyles}>
              {documentToReactComponents(overview.json, getOptions(overview, deviceWidth))}
            </div>
          </section>
        )}
      </div>
      {((product && product.length > 0) || hasRoles) && (
        <div css={metadataContainerStyles} data-testid="metadata-container">
          {product && product.length > 0 && (
            <div css={metadataGroupStyles} data-testid="products-group">
              <span css={metadataLabelStyles} data-testid="products-label">
                PRODUCTS
              </span>
              <div css={metadataItemsStyles} data-testid="products-items">
                <TagList isLarge={true} taxonomyList={product || []} />
              </div>
            </div>
          )}
          {hasRoles && (
            <div css={metadataGroupStyles} data-testid="role-group">
              <span css={metadataLabelStyles} data-testid="role-label">
                ROLE
              </span>
              <div css={metadataItemsStyles} data-testid="role-items">
                <span css={metadataValueStyles} data-testid="role-value">
                  {(productRole || []).join(', ')}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const headerContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: token('space.300'),
  paddingBottom: '18px',

  '@media (max-width: 965px)': {
    paddingBottom: token('space.300'),
    width: '100%',
  },
});

const headingAndSubheadingStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: token('space.050'),
  flex: '1 0 0',
});

const breadcrumbContainerStyles = css({
  display: 'flex',
  minHeight: '24px',
  alignItems: 'center',
  gap: token('space.100'),

  '& a, & span, & nav, & li': {
    color: token('color.text.subtle'),
    textAlign: 'center',
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
  },

  li: {
    alignItems: 'center',

    '&::after': {
      content: '"/" !important',
      padding: `0 ${token('space.100')}`,
    },
  },
});

const metadataContainerStyles = css({
  display: 'flex',
  alignItems: 'baseline',
  gap: token('space.300'),

  '@media (max-width: 965px)': {
    flexDirection: 'column',
    gap: token('space.100'),
    borderTop: `1px solid ${token('color.border')}`,
    borderBottom: `1px solid ${token('color.border')}`,
    padding: `${token('space.200')}  0`,
    width: '100%',
  },
});

const metadataGroupStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: token('space.050'),

  '@media (max-width: 965px)': {
    display: 'flex',
    gap: token('space.200'),
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    alignItems: 'baseline',
  },
});

const metadataItemsStyles = css({
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',

  '@media (max-width: 965px)': {
    flex: 1,
    gap: token('space.050'),
    paddingLeft: token('space.100'),
  },
});

const metadataLabelStyles = css({
  color: '#172B4D',
  fontFamily: 'Charlie Display, sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: '24px',

  '@media (max-width: 965px)': {
    fontSize: '14px',
    lineHeight: '20px',
    minWidth: '80px',
    flexShrink: 0,
  },
});

const subHeadingStyles = css({
  color: '#172B4D',
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '32px',
});

const metadataValueStyles = css({
  color: '#172B4D',
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '32px',

  '@media (max-width: 965px)': {
    fontSize: '14px',
    lineHeight: '20px',
  },
});

const markdownContainerStyles = css({
  margin: '0 !important',
});

const learningPathOverviewStyles = css({
  width: '100%',
  margin: '0 !important',
  color: N800,

  p: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: '400',
    fontSize: '20px',
    lineHeight: '32px',
    color: N800,
  },
  ul: {
    fontFamily: 'Inter, sans-serif',
    listStyleType: 'disc',
    paddingLeft: '30px !important',
    color: N800,
  },
  'ul li': {
    fontSize: '20px',
    marginTop: token('space.050'),
    lineHeight: '32px',

    '&:first-child': {
      marginTop: 0,
    },
  },
  'ol li': {
    fontSize: '20px',
    lineHeight: '32px',
    marginTop: token('space.050'),

    '&:first-child': {
      marginTop: 0,
    },
  },
  'ul ul': {
    listStyleType: 'circle',
  },
  'ul ul ul': {
    listStyleType: 'square',
  },
  ol: {
    listStyleType: 'decimal',
    paddingLeft: '30px !important',
    color: N800,
  },
  'ol ol': {
    listStyleType: 'lower-alpha',
  },
  'ol ol ol': {
    listStyleType: 'lower-roman',
  },
  a: {
    fontFamily: 'Inter, sans-serif',
    fontSize: '20px',
    color: token('color.text.accent.blue'),
    textUnderlineOffset: '4px',
    textDecorationThickness: '2px',
    textDecorationColor: token('color.link'),
    textDecorationLine: 'none',
    cursor: 'pointer',
    ':hover': {
      color: token('color.link'),
      textDecorationLine: 'underline',
    },
  },

  [media.below.sm]: {
    p: {
      fontSize: '16px',
      lineHeight: '24px',
    },
    ul: {
      paddingLeft: `${token('space.250')} !important`,
    },
    ol: {
      paddingLeft: `${token('space.250')} !important`,
    },
    'ul li': {
      fontSize: '14px',
      lineHeight: '20px',
    },
    'ol li': {
      fontSize: '14px',
      lineHeight: '20px',
    },
  },

  h3: {
    fontFamily: 'Charlie Display, sans-serif',
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: '32px',
    color: N800,
    margin: 0,

    [media.above.sm]: {
      fontSize: '32px',
      lineHeight: '40px',
    },
  },

  hr: {
    margin: `${token('space.200')} 0`,
  },
});
